import { useState } from "react";
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MessageCircle, Monitor, Settings, Server, Shield, Laptop, Wifi, Printer, HardDrive, ChevronDown, CheckCircle, HeadphonesIcon, Wrench, Building2, Users, Calendar, Briefcase } from "lucide-react";

const BUTTON_STYLES = {
  base: "px-6 py-3 rounded text-white transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
  primary: "bg-[#0078D4] hover:bg-[#005bb5] active:bg-[#004494]",
  secondary: "bg-gray-600 hover:bg-gray-700 active:bg-gray-800"
};

const INPUT_STYLES = "w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200";

const CARD_STYLES = "p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-100";

const texts = {
  heroTitle: "Soporte Técnico Informático en Santiago",
  heroSubtitle: "Soluciones TI profesionales para empresas y particulares. Formateo de PC, instalación de impresoras, redes, mantenimiento preventivo y soporte técnico a Domicilio u Oficina. Atención rápida en Región Metropolitana.",
  buttonStart: "Solicitar Cotización",
  buttonExplore: "Ver Servicios",
  contactTitle: "Solicita Tu Cotización",
  footer: "© 2026 SuperCloud Chile. Todos los derechos reservados.",
  placeholderName: "Nombre completo",
  placeholderEmail: "Correo electrónico",
  placeholderMessage: "Describe tu problema: ¿PC lento? ¿Impresora que no funciona? ¿Necesitas formateo o instalación de red?",
  services: [
    { 
      title: "Soporte Técnico a Domicilio u Oficina", 
      description: "Reparación de computadores, notebooks y equipos informáticos en tu casa u oficina. Diagnóstico, limpieza de virus, instalación de programas y solución de problemas.",
      icon: <Wrench size={48} /> 
    },
    { 
      title: "Formateo de Computadores", 
      description: "Formateo profesional de PC y notebooks. Instalación de Windows, drivers, programas básicos y respaldo de información. Tu equipo como nuevo.",
      icon: <HardDrive size={48} /> 
    },
    { 
      title: "Instalación de Impresoras y Escáneres", 
      description: "Configuración de impresoras en red, instalación de drivers, conexión WiFi y solución de problemas de impresión en Windows y Mac.",
      icon: <Printer size={48} /> 
    },
    { 
      title: "Instalación y Configuración de Redes", 
      description: "Instalación de redes WiFi, cableado estructurado, configuración de routers y puntos de acceso. Redes seguras para tu oficina o negocio.",
      icon: <Wifi size={48} /> 
    },
    { 
      title: "Mantenimiento Preventivo", 
      description: "Limpieza física de equipos, actualización de software, optimización de rendimiento y revisión de seguridad para evitar problemas futuros.",
      icon: <Settings size={48} /> 
    },
    { 
      title: "Soporte Técnico Remoto", 
      description: "Asistencia técnica por acceso remoto para solucionar problemas urgentes sin salir de tu casa. Ideal para configuraciones y consultas rápidas.",
      icon: <HeadphonesIcon size={48} /> 
    },
  ],
  businessServices: [
    {
      title: "Soporte TI Empresarial",
      description: "Soluciones integrales de tecnología para empresas y pymes en Santiago y Región Metropolitana. Gestión completa de infraestructura TI y soporte especializado.",
      icon: <Building2 size={48} />
    },
    {
      title: "Mantenimiento Preventivo Empresarial",
      description: "Planes programados de mantenimiento para servidores, estaciones de trabajo y equipos críticos. Minimiza interrupciones y maximiza la productividad empresarial.",
      icon: <Settings size={48} />
    },
    {
      title: "Redes Empresariales",
      description: "Diseño, instalación y administración de redes corporativas en Santiago. Cableado estructurado, WiFi empresarial y seguridad perimetral para oficinas.",
      icon: <Server size={48} />
    },
    {
      title: "Contratos de Soporte Mensual",
      description: "Soporte técnico continuo con planes mensuales personalizados. Mesa de ayuda, soporte remoto y visitas programadas para empresas en la Región Metropolitana.",
      icon: <Calendar size={48} />
    }
  ],
  problems: [
    {
      title: "¿Tu computador está lento o se congela constantemente?",
      description: "Diagnosticamos y solucionamos problemas de rendimiento. Limpiamos virus, optimizamos el sistema y recuperamos la velocidad de tu PC o notebook."
    },
    {
      title: "¿La impresora no imprime o muestra errores?",
      description: "Instalamos y configuramos impresoras de cualquier marca. Solucionamos problemas de conexión, drivers y compartición en red."
    },
    {
      title: "¿Necesitas instalar o reparar tu red WiFi?",
      description: "Instalamos redes inalámbricas seguras, ampliamos la cobertura WiFi y solucionamos problemas de conexión a internet en tu oficina o casa."
    },
    {
      title: "¿Tu equipo tiene virus o necesita formateo urgente?",
      description: "Eliminamos virus y malware, respaldamos tu información importante y formateamos tu computador dejándolo limpio y funcionando perfectamente."
    }
  ],
  whyUs: [
    "Más de 15 años de experiencia en soporte técnico en Santiago",
    "Atención a Domicilio u Oficina en toda la Región Metropolitana",
    "Diagnóstico gratuito antes de cualquier reparación",
    "Presupuesto claro y sin sorpresas",
    "Garantía en todos nuestros servicios técnicos",
    "Respuesta rápida: atención el mismo día en casos urgentes"
  ],
  faqs: [
    {
      question: "¿Cuánto cuesta el servicio técnico a domicilio?",
      answer: "El costo varía según el tipo de servicio. Un formateo de PC tiene un precio aproximado de $25.000 a $35.000. La instalación de impresoras desde $15.000. Para servicios más complejos como redes o mantenimiento, enviamos un técnico para evaluar y dar presupuesto sin compromiso."
    },
    {
      question: "¿Cuánto demora un formateo de computador?",
      answer: "Un formateo completo con instalación de Windows, drivers y programas básicos toma entre 2 a 4 horas. Si incluye respaldo de información, puede tomar medio día. Coordinamos el horario que mejor te acomode."
    },
    {
      question: "¿Atienden a Domicilio u Oficina en toda Santiago?",
      answer: "Sí, atendemos en toda la Región Metropolitana incluyendo Santiago Centro, Providencia, Las Condes, Ñuñoa, La Florida, Maipú, Puente Alto y comunas cercanas. Para comunas más alejadas, consulta disponibilidad."
    },
    {
      question: "¿Qué incluye el mantenimiento preventivo?",
      answer: "El mantenimiento incluye: limpieza física interna del equipo, eliminación de programas innecesarios, actualización de software, optimización del sistema, revisión de seguridad y recomendaciones para mejorar el rendimiento."
    },
    {
      question: "¿Tienen garantía los servicios técnicos?",
      answer: "Sí, todos nuestros servicios tienen garantía. El formateo tiene 30 días de garantía por fallas del sistema operativo. Las reparaciones de hardware tienen garantía según el tipo de componente. Si el problema persiste, volvemos sin costo adicional."
    }
  ],
  testimonials: [
    {
      name: "María González",
      company: "Particular - Ñuñoa",
      text: "Mi notebook estaba lentísimo. Vinieron a casa, lo formatearon y quedó perfecto. Muy profesionales y el precio justo."
    },
    {
      name: "Jorge Martínez",
      company: "Pyme - Providencia",
      text: "Nos instalaron toda la red en la oficina. Ahora funciona perfecto en todos los pisos. Recomendados 100%."
    },
    {
      name: "Claudia Rojas",
      company: "Contadora - Las Condes",
      text: "Tenía problemas con la impresora que no se conectaba. En una hora lo solucionaron por acceso remoto. Excelente servicio."
    }
  ]
};

export default function SuperCloudLanding() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = "El nombre es requerido";
    }
    
    if (!formData.email.trim()) {
      errors.email = "El correo electrónico es requerido";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Por favor ingresa un correo electrónico válido";
      }
    }
    
    if (!formData.message.trim()) {
      errors.message = "El mensaje es requerido";
    }
    
    return errors;
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  const errors = validateForm();
  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }

  setIsSubmitting(true);

  try {
    const response = await fetch("/contact.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        message: formData.message,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      alert("❌ Error al enviar el mensaje");
      return;
    }

    alert("✅ Mensaje enviado correctamente");
    setFormData({ name: "", email: "", message: "" });

  } catch (error) {
    alert("❌ Error de conexión con el servidor");
  } finally {
    setIsSubmitting(false);
  }
};

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "SuperCloud - Soporte Técnico Informático Santiago",
    "image": "https://supercloud.cl/logo.png",
    "@id": "https://supercloud.cl",
    "url": "https://supercloud.cl",
    "telephone": "+56-937719159",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Santiago",
      "addressRegion": "Región Metropolitana",
      "addressCountry": "CL"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -33.4489,
      "longitude": -70.6693
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "09:00",
      "closes": "19:00"
    },
    "sameAs": [
      "https://wa.me/56937719159"
    ],
    "areaServed": {
      "@type": "State",
      "name": "Región Metropolitana"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios de Soporte Técnico",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Soporte Técnico a Domicilio u Oficina",
            "description": "Reparación y mantenimiento de computadores en Santiago"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Formateo de Computadores",
            "description": "Formateo profesional de PC y notebooks con instalación de Windows"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Instalación de Redes",
            "description": "Instalación y configuración de redes WiFi empresariales"
          }
        }
      ]
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": texts.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Soporte Técnico Informático Santiago | Formateo PC, Redes, Impresoras</title>
        
        <meta 
          name="description" 
          content="Soporte técnico informático en Santiago. Formateo de PC, instalación de impresoras, redes WiFi, mantenimiento preventivo y reparación a Domicilio u Oficina. Atención rápida en Región Metropolitana." 
        />
        
        <link rel="canonical" href="https://supercloud.cl" />
        
        <meta property="og:title" content="Soporte Técnico Informático en Santiago | SuperCloud" />
        <meta property="og:description" content="Formateo de PC, instalación de impresoras y redes, soporte técnico a Domicilio u Oficina en Santiago. Cotiza gratis." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://supercloud.cl" />
        <meta property="og:image" content="https://supercloud.cl/og-image.jpg" />
        <meta property="og:locale" content="es_CL" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Soporte Técnico Informático Santiago | SuperCloud" />
        <meta name="twitter:description" content="Soluciones TI para empresas y particulares en Región Metropolitana" />
        
        <meta name="geo.region" content="CL-RM" />
        <meta name="geo.placename" content="Santiago" />
        <meta name="geo.position" content="-33.4489;-70.6693" />
        <meta name="ICBM" content="-33.4489, -70.6693" />
      </Helmet>

      <div className="min-h-screen bg-white text-gray-900">
        
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        <nav className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-6 py-4 sticky top-0 z-50 shadow-lg">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo.png" 
                alt="SuperCloud Soporte Técnico" 
                className="w-10 h-auto"
                loading="eager"
              />
              <span className="text-xl font-semibold">SuperCloud</span>
            </div>
            <ul className="hidden md:flex space-x-6 text-sm font-medium">
              <li>
                <button 
                  onClick={() => scrollToSection('services')} 
                  className="hover:text-blue-300 transition-colors duration-200"
                >
                  Servicios
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('empresas')} 
                  className="hover:text-blue-300 transition-colors duration-200"
                >
                  Empresas
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('nosotros')} 
                  className="hover:text-blue-300 transition-colors duration-200"
                >
                  Nosotros
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('testimonios')} 
                  className="hover:text-blue-300 transition-colors duration-200"
                >
                  Testimonios
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contact-form')} 
                  className="hover:text-blue-300 transition-colors duration-200"
                >
                  Cotizar
                </button>
              </li>
            </ul>
            <a 
              href="https://wa.me/56937719159" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 hover:text-blue-300 transition"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="hidden md:inline text-sm font-semibold">WhatsApp</span>
            </a>
          </div>
        </nav>

        <section className="bg-gradient-to-r from-[#E3F5C8] via-[#C7E8A9] to-[#6DC8D9] py-20 px-6 md:px-20">
          <div className="container mx-auto max-w-4xl text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight text-gray-900">
              {texts.heroTitle}
            </h1>
            <p className="text-xl mt-6 text-gray-800 max-w-3xl">
              {texts.heroSubtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <button 
                className={`${BUTTON_STYLES.base} ${BUTTON_STYLES.primary}`}
                onClick={() => scrollToSection('contact-form')}
                aria-label="Solicitar cotización de soporte técnico"
              >
                {texts.buttonStart}
              </button>
              <button 
                className={`${BUTTON_STYLES.base} ${BUTTON_STYLES.secondary}`}
                onClick={() => scrollToSection('services')}
                aria-label="Ver servicios de soporte técnico"
              >
                {texts.buttonExplore}
              </button>
            </div>
            <p className="mt-6 text-gray-700 text-lg">
               Atención rápida |  Diagnóstico gratuito | Atención en toda la RM
            </p>
          </div>
        </section>

        <section className="py-16 px-6 bg-gradient-to-br from-gray-50 to-slate-50">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              ¿Problemas con tu Computador o Red? Te Ayudamos
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {texts.problems.map((problem, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md border-l-4 border-[#0078D4]">
                  <h3 className="text-xl font-bold mb-3 text-gray-800">{problem.title}</h3>
                  <p className="text-gray-600">{problem.description}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <button 
                onClick={() => scrollToSection('contact-form')}
                className={`${BUTTON_STYLES.base} ${BUTTON_STYLES.primary}`}
              >
                {texts.buttonStart}
              </button>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold text-center mb-4 text-gray-800">
              ¿Por qué Elegir SuperCloud?
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Soporte técnico profesional para empresas y particulares en Santiago
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {[
                { icon: Wrench, title: "Soporte a Domicilio u Oficina", desc: "Vamos donde estés en la Región Metropolitana" },
                { icon: Settings, title: "Diagnóstico Gratuito", desc: "Evaluamos tu equipo sin costo antes de reparar" },
                { icon: CheckCircle, title: "Garantía Incluida", desc: "Respaldamos la calidad de nuestro trabajo" }
              ].map((item, index) => (
                <div key={index} className={`${CARD_STYLES} text-center`}>
                  <item.icon className="text-[#0078D4] mx-auto mb-4" size={48} />
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="services" className="py-16 px-6 md:px-20 bg-gradient-to-br from-slate-50 to-gray-100">
          <div className="container mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-4">
              Servicios de Soporte Técnico Informático
            </h2>
            <p className="text-center text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
              Soluciones completas de TI para empresas y particulares en Santiago y Regiones
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {texts.services.map((service, index) => (
                <article key={index} className={`${CARD_STYLES} flex flex-col`}>
                  <div className="text-[#0078D4] mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-800">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="empresas" className="py-16 px-6 md:px-20 bg-white">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Soluciones Corporativas
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Servicios empresas
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Soporte TI especializado para pymes en Santiago.
                Mantenimiento preventivo, soporte continuo y soluciones tecnológicas corporativas a Domicilio u Oficina.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {texts.businessServices.map((service, index) => (
                <article key={index} className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-lg shadow-md hover:shadow-xl transition border border-gray-200">
                  <div className="text-[#0078D4] mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-800">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </article>
              ))}
            </div>

            <div className="bg-gradient-to-r from-[#0078D4] to-[#005bb5] rounded-2xl p-8 md:p-12 text-white">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">
                    ¿Tu empresa necesita soporte TI confiable?
                  </h3>
                  <p className="text-blue-100 mb-6">
                    Ofrecemos planes mensuales de mantenimiento preventivo y soporte técnico continuo 
                    para empresas en Santiago. Contratos flexibles adaptados a las necesidades de tu pyme.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 mr-3 text-green-300" />
                      <span>Soporte técnico remoto y presencial</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 mr-3 text-green-300" />
                      <span>Mantenimiento programado de equipos</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 mr-3 text-green-300" />
                      <span>Administración de redes empresariales</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 mr-3 text-green-300" />
                      <span>Planes personalizados para pymes en Santiago</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                  <Users className="w-12 h-12 text-blue-200 mb-4" />
                  <h4 className="text-xl font-bold mb-3">Contratos Mensuales</h4>
                  <p className="text-blue-100 mb-4">
                    Desde 3 hasta 20+ equipos. Cotización personalizada según las necesidades de tu empresa en la Región Metropolitana.
                  </p>
                  <button 
                    onClick={() => scrollToSection('contact-form')}
                    className="w-full bg-white text-[#0078D4] px-6 py-3 rounded-lg font-bold hover:bg-blue-50 transition"
                  >
                    Solicitar Cotización
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="nosotros" className="py-16 px-6 bg-gradient-to-br from-gray-50 to-slate-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              ¿Por qué Confiar en Nosotros?
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {texts.whyUs.map((reason, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700 text-lg">{reason}</p>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-8 rounded-lg shadow-md border border-gray-200">
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Experiencia en Soporte Técnico</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Llevamos más de 15 años brindando soporte técnico informático en Santiago y la Región Metropolitana. 
                Atendemos tanto a empresas como a particulares que necesitan solucionar problemas con sus equipos, 
                redes o impresoras de forma rápida y profesional.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Nuestro equipo de técnicos certificados entiende las necesidades de cada cliente. Ya sea un formateo 
                urgente, la instalación de una red WiFi o el mantenimiento preventivo de tu equipo, nos comprometemos 
                a entregar un servicio de calidad con garantía.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
              Preguntas Frecuentes sobre Soporte Técnico
            </h2>
            <p className="text-center text-gray-600 mb-12">
              Resolvemos las dudas más comunes sobre nuestros servicios
            </p>
            <div className="space-y-4">
              {texts.faqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden bg-gradient-to-r from-gray-50 to-slate-50">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex justify-between items-center p-6 hover:bg-gray-100 transition text-left"
                    aria-expanded={openFaq === index}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <h3 className="text-lg font-bold text-gray-800 pr-4">{faq.question}</h3>
                    <ChevronDown 
                      className={`w-5 h-5 text-[#0078D4] flex-shrink-0 transition-transform ${
                        openFaq === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaq === index && (
                    <div 
                      id={`faq-answer-${index}`}
                      className="p-6 bg-white border-t border-gray-200"
                    >
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="testimonios" className="py-16 px-6 bg-gradient-to-br from-slate-50 to-gray-100">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Lo Que Dicen Nuestros Clientes
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {texts.testimonials.map((testimonial, index) => (
                <article key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <div className="flex items-center mb-4">
                    <MessageCircle className="w-8 h-8 text-[#0078D4] mr-3" />
                    <div>
                      <p className="font-bold text-gray-800">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.company}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="contact-form" className="bg-gradient-to-r from-[#0078D4] to-[#005bb5] py-16 px-6 md:px-20">
          <div className="container mx-auto max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
              {texts.contactTitle}
            </h2>
            <p className="text-center text-blue-100 mb-8 text-lg">
              Te respondemos rápido con un presupuesto sin compromiso
            </p>
            
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block mb-2 font-semibold text-gray-800">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder={texts.placeholderName}
                    value={formData.name}
                    onChange={handleChange}
                    className={`${INPUT_STYLES} ${formErrors.name ? 'border-red-500 ring-red-500' : ''}`}
                    disabled={isSubmitting}
                    autoComplete="name"
                  />
                  {formErrors.name && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block mb-2 font-semibold text-gray-800">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder={texts.placeholderEmail}
                    value={formData.email}
                    onChange={handleChange}
                    className={`${INPUT_STYLES} ${formErrors.email ? 'border-red-500 ring-red-500' : ''}`}
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                  {formErrors.email && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block mb-2 font-semibold text-gray-800">
                    Cuéntanos Qué Necesitas
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder={texts.placeholderMessage}
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className={`${INPUT_STYLES} resize-none ${formErrors.message ? 'border-red-500 ring-red-500' : ''}`}
                    disabled={isSubmitting}
                  />
                  {formErrors.message && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full ${BUTTON_STYLES.base} ${BUTTON_STYLES.primary} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Enviando...' : 'Solicitar Cotización'}
                </button>

                <p className="text-center text-sm text-gray-600">
                  ✓ Respuesta rápida<br />
                  ✓ Presupuesto sin compromiso<br />
                  ✓ Diagnóstico gratuito
                </p>
              </div>
            </form>
          </div>
        </section>

        <footer className="bg-gray-900 text-white py-12">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-bold mb-4">SuperCloud</h3>
                <p className="text-gray-400 mb-4">
                  Soporte técnico informático profesional en Santiago. 
                  Formateo de PC, instalación de redes y impresoras, mantenimiento preventivo.
                </p>
                <p className="text-gray-400">
                  Más de 15 años de experiencia atendiendo empresas y particulares 
                  en toda la Región Metropolitana.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-4">Contacto</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4" />
                    <a href="tel:+56937719159" className="hover:text-[#0078D4] transition">
                      +56 937719159
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MessageCircle className="w-4 h-4" />
                    <a 
                      href="https://wa.me/56937719159" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-[#0078D4] transition"
                    >
                      WhatsApp
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <a 
                      href="mailto:contacto@supercloud.cl"
                      className="hover:text-[#0078D4] transition"
                    >
                      contacto@supercloud.cl
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Horario de Atención</h3>
                <p className="text-gray-400">
                  <strong className="text-white">Lunes a Viernes:</strong><br />
                  9:00 - 19:00 hrs
                </p>
                <p className="text-gray-400 mt-4">
                  <strong className="text-white">Sábado:</strong><br />
                  10:00 - 14:00 hrs
                </p>
                <p className="text-gray-400 mt-4 text-sm">
                  Atención de urgencias coordinando previamente
                </p>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-8 text-center">
              <p className="mb-4">{texts.footer}</p>
              <div className="flex justify-center space-x-6 mb-4">
                <a 
                  href="tel:+56937719159" 
                  aria-label="Llamar a SuperCloud"
                  className="text-2xl hover:text-[#0078D4] transition-colors duration-200"
                >
                  <Phone size={32} />
                </a>
                <a 
                  href="https://wa.me/56937719159" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="WhatsApp SuperCloud"
                  className="text-2xl hover:text-[#25D366] transition-colors duration-200"
                >
                  <MessageCircle size={32} />
                </a>
                <a 
                  href="mailto:contacto@supercloud.cl" 
                  aria-label="Email SuperCloud"
                  className="text-2xl hover:text-[#0078D4] transition-colors duration-200"
                >
                  <Mail size={32} />
                </a>
              </div>
              <p className="text-sm text-gray-400">
                Soporte Técnico Santiago | Formateo PC | Instalación Impresoras | Redes WiFi | Mantenimiento Computadores | Soporte Empresarial
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}